import { useState } from 'react';
import { Badge, Button } from 'reactstrap';
import { AIChatQueryResponse } from '../../../../api/initiative-data-integrations';
import { BasicAlert } from '@g17eco/molecules/alert';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: Partial<AIChatQueryResponse> &
    {
      error?: string;
    };
}

interface AIMessageMetadataProps {
  metadata: ChatMessage['metadata'];
  messageId: string;
  onSuggestionClick?: (question: string) => void;
}

export const AIMessageMetadata: React.FC<AIMessageMetadataProps> = ({ metadata, onSuggestionClick }) => {
  const [showSQL, setShowSQL] = useState(false);

  if (!metadata) {
    return null;
  }

  const renderDataTable = (data: AIChatQueryResponse['data']) => {
    if (!data) {
      return null;
    }

    if (data.length === 0) {
      return (
        <div className='data-table-container mb-2'>
          <small className='text-muted d-block mb-1'>
            <i className='fal fa-table me-1' />
            Query Results (0 fields):
          </small>
          <BasicAlert type='warning'>No data has been found matching your query.<br />If you think this is not correct, please rephrase your question.</BasicAlert>
        </div>
      );
    }

    // Transform key-value pairs into a single row table with keys as columns
    const keys = Object.keys(data[0]);

    return (
      <div className='data-table-container mb-2'>
        <small className='text-muted d-block mb-1'>
          <i className='fal fa-table me-1' />
          Query Results ({data.length} fields):
        </small>
        <div className='table-responsive' style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <table className='table table-sm table-striped mb-0'>
            <thead className='table-dark sticky-top'>
              <tr>
                {keys.map((key, idx) => (
                  <th key={idx} style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx: number) => (
                <tr key={idx}>
                  {keys.map((key, idy: number) => (
                    <td key={`${idx}-${idy}`}>
                      {row[key] !== null && row[key] !== undefined ? row[key]?.toString() : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className='mt-2 pt-2 border-top border-light'>
      {/* Data Results Table */}
      {metadata.data ? renderDataTable(metadata.data) : null}

      {/* SQL Query (Collapsible) */}
      {metadata.sql_query && (
        <div className='mb-2'>
          <div className='d-flex justify-content-between align-items-center'>
            <Button
              color='link-secondary'
              size='xs'
              onClick={() => setShowSQL(!showSQL)}
            >
            <i className={'fal fa-code me-1'} />
            <i className={`fal ${showSQL ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} />
            View SQL Query
          </Button>
          {metadata.response_metadata?.model_names && metadata.response_metadata?.model_names.length > 0 && (
           metadata.response_metadata?.model_names.map(model => (
            <Badge color='secondary' className='bg-opacity-25 ms-2'>
              {model}
            </Badge>
           ))
          )}
          </div>
          {showSQL && (
            <pre className='small bg-dark text-light p-2 rounded mt-1 mb-0' style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {metadata.sql_query}
            </pre>
          )}
        </div>
      )}

      {/* Suggested Questions */}
      {metadata.suggested_questions && metadata.suggested_questions.length > 0 && (
        <div className='mb-2'>
          <small className='text-muted d-block mb-1'>
            <i className='fal fa-lightbulb me-1' />
            Suggested follow-up questions:
          </small>
          <div className='d-flex flex-wrap gap-1'>
            {metadata.suggested_questions.map((question: string, idx: number) => (
              <Button
                color='primary'
                outline
                key={idx}
                style={{ padding: '2px 8px' }}
                onClick={() => onSuggestionClick?.(question)}
                title={`Click to ask: ${question}`}
              >
                <i className='fal fa-comment-dots me-1' />
                {question}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {metadata.error && (
        <div className='mb-2'>
          <small className='text-danger d-block mb-1'>
            <i className='fal fa-exclamation-triangle me-1' />
            Error:
          </small>
          <div className='text-danger small'>{metadata.error}</div>
        </div>
      )}
    </div>
  );
};
